import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Settings, 
  Settings2, 
  Save,
  LayoutDashboard,
  Users,
  Phone,
  AlertTriangle,
  Cloud,
  FolderOpen,
  Activity,
  UserCircle,
  BookOpen,
  FileBarChart,
  GraduationCap,
  Trophy,
  FileText,
  UserSearch,
  FileAudio,
  Key,
  Brain,
  Loader2,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Mail,
  Server,
  Shield,
  ShieldCheck,
  Lock,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  Download,
  Search,
  Filter,
  Calendar,
  Clock,
  AlertCircle,
  UserCheck,
  UserX,
  MapPin,
  Building2,
  Zap,
  BarChart3,
  TrendingUp,
  DollarSign,
  Cpu,
  HardDrive,
  Database,
  Gauge,
  Network,
  Timer,
  Megaphone,
  Presentation
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts';
import { getApiKeys, createApiKey, deleteApiKey, regenerateApiKey, getAuditLogs, getAuditLogActions, exportAuditLogs, getDOBSSNExceptions, addDOBSSNException, removeDOBSSNException, getUsers, refreshAllConfigurations, getSystemStatus, getLLMUsageSummary, getLLMUsageByProvider, getLLMUsageDaily, getTenantRetentionPolicy, updateTenantRetentionPolicy, resetTenantRetentionPolicy, getPendingPurgeData, triggerManualPurge, getPurgeLogs } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import APIConfiguration from './APIConfiguration';
import AudioDecodingSettings from './AudioDecodingSettings';
import LocationManagement from './LocationManagement';
import RoleManagement from './RoleManagement';
import NotificationSettings from '@/components/NotificationSettings';
import SecuritySettings from './SecuritySettings';
import { PresentationDownload } from '@/components/PresentationDownload';

// DOB/SSN Exception Configuration Component
function DOBSSNExceptionConfiguration() {
  const { tenant, user } = useAuth();
  const [exceptions, setExceptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [reason, setReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (tenant?.id) {
      loadData();
    }
  }, [tenant?.id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [exceptionsRes, usersRes] = await Promise.all([
        getDOBSSNExceptions(tenant.id),
        getUsers()
      ]);
      setExceptions(exceptionsRes.data.exceptions || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to load DOB/SSN exceptions:', error);
      toast.error('Failed to load exceptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddException = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setIsAdding(true);
    try {
      await addDOBSSNException(tenant.id, {
        user_id: selectedUserId,
        reason: reason || undefined,
        added_by: user?.id
      });
      toast.success('User added to DOB/SSN exception list');
      setShowAddDialog(false);
      setSelectedUserId('');
      setReason('');
      await loadData();
    } catch (error) {
      console.error('Failed to add exception:', error);
      toast.error(error.response?.data?.detail || 'Failed to add exception');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveException = async (exceptionId, userName) => {
    if (!confirm(`Are you sure you want to remove ${userName} from the DOB/SSN exception list?`)) {
      return;
    }

    try {
      await removeDOBSSNException(tenant.id, exceptionId);
      toast.success('User removed from exception list');
      await loadData();
    } catch (error) {
      console.error('Failed to remove exception:', error);
      toast.error('Failed to remove exception');
    }
  };

  // Filter users for autocomplete - exclude already excepted users
  const exceptedUserIds = exceptions.map(e => e.user_id);
  const availableUsers = users.filter(u => 
    !exceptedUserIds.includes(u.id) &&
    (searchQuery === '' || 
     u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-600" />
              DOB/SSN Exception List
            </span>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add User Exception
            </Button>
          </CardTitle>
          <CardDescription>
            Users on this list are permitted to ask for DOB/SSN during calls without triggering compliance alerts.
            This is typically for authorized personnel who need this information for legitimate business purposes.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Exception List */}
      {exceptions.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <UserCheck className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-muted-foreground">No Exception Users</h3>
              <p className="text-sm mt-1">Add users who are authorized to collect DOB/SSN information</p>
              <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Exception
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-foreground">
              {exceptions.length} User{exceptions.length !== 1 ? 's' : ''} Authorized
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {exceptions.map((exception) => (
                <div 
                  key={exception.id} 
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-emerald-100 rounded-full">
                      <UserCheck className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{exception.user_name}</h4>
                      <p className="text-sm text-muted-foreground">{exception.user_email}</p>
                      {exception.reason && (
                        <p className="text-xs text-muted-foreground mt-1">Reason: {exception.reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-xs text-muted-foreground">
                      <p>Added by {exception.added_by_name || 'Admin'}</p>
                      <p>{new Date(exception.created_at).toLocaleDateString()}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRemoveException(exception.id, exception.user_name)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <UserX className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Exception Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-emerald-600" />
                Add DOB/SSN Exception
              </CardTitle>
              <CardDescription>
                Select a user to authorize for DOB/SSN collection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Search User</Label>
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Select User</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">No users found</div>
                    ) : (
                      availableUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Input
                  placeholder="e.g., Authorized for background verification"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddDialog(false);
                  setSelectedUserId('');
                  setReason('');
                  setSearchQuery('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddException} disabled={isAdding || !selectedUserId}>
                {isAdding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add Exception
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Email Configuration Component
function EmailConfiguration() {
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [editingConfig, setEditingConfig] = useState(null);
  
  const [newConfig, setNewConfig] = useState({
    name: '',
    provider_type: 'smtp',
    email: '',
    password: '',
    host: '',
    port: 587,
    use_ssl: true,
    use_tls: true,
    is_default: false,
    exchange_server: '',
    exchange_domain: ''
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/email/configs`);
      if (res.ok) {
        const data = await res.json();
        setEmailConfigs(data.configs || []);
      }
    } catch (error) {
      console.error('Failed to load email configs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async (config) => {
    setIsSaving(true);
    try {
      const method = config.id ? 'PUT' : 'POST';
      const url = config.id 
        ? `${process.env.REACT_APP_BACKEND_URL || ''}/api/email/configs/${config.id}`
        : `${process.env.REACT_APP_BACKEND_URL || ''}/api/email/configs`;
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (res.ok) {
        toast.success(config.id ? 'Email configuration updated' : 'Email configuration added');
        setShowAddEmail(false);
        setEditingConfig(null);
        setNewConfig({
          name: '', provider_type: 'smtp', email: '', password: '',
          host: '', port: 587, use_ssl: true, use_tls: true, is_default: false,
          exchange_server: '', exchange_domain: ''
        });
        await loadConfigs();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save email configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteConfig = async (configId) => {
    if (!confirm('Are you sure you want to delete this email configuration?')) return;
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/email/configs/${configId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Email configuration deleted');
        await loadConfigs();
      }
    } catch (error) {
      toast.error('Failed to delete email configuration');
    }
  };

  const testConfig = async (configId) => {
    setIsTesting(true);
    setTestResults({ ...testResults, [configId]: 'testing' });
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/email/test/${configId}`, {
        method: 'POST'
      });
      const data = await res.json();
      setTestResults({ ...testResults, [configId]: data.success ? 'success' : 'failed' });
      if (data.success) {
        toast.success('Email connection successful');
      } else {
        toast.error(`Connection failed: ${data.error}`);
      }
    } catch (error) {
      setTestResults({ ...testResults, [configId]: 'failed' });
      toast.error('Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const setDefaultConfig = async (configId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/email/configs/${configId}/set-default`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Default email updated');
        await loadConfigs();
      }
    } catch (error) {
      toast.error('Failed to set default email');
    }
  };

  const getProviderIcon = (type) => {
    switch (type) {
      case 'exchange': return <Server className="h-5 w-5 text-blue-600" />;
      case 'pop': return <Mail className="h-5 w-5 text-amber-600" />;
      case 'imap': return <Mail className="h-5 w-5 text-purple-600" />;
      default: return <Mail className="h-5 w-5 text-emerald-600" />;
    }
  };

  const getTestIcon = (configId) => {
    const result = testResults[configId];
    if (result === 'testing') return <Loader2 className="h-4 w-4 animate-spin" />;
    if (result === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (result === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
    return null;
  };

  const getDefaultPorts = (type) => {
    switch (type) {
      case 'smtp': return { port: 587, ssl: 465 };
      case 'pop': return { port: 110, ssl: 995 };
      case 'imap': return { port: 143, ssl: 993 };
      case 'exchange': return { port: 443, ssl: 443 };
      default: return { port: 587, ssl: 465 };
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const configForm = editingConfig || newConfig;
  const setConfigForm = editingConfig ? setEditingConfig : setNewConfig;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Email Configurations
            </span>
            <Button onClick={() => setShowAddEmail(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Email Account
            </Button>
          </CardTitle>
          <CardDescription>
            Configure email accounts for sending notifications and reports. Supports SMTP, Exchange, POP, and IMAP.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Existing Configurations */}
      {emailConfigs.length === 0 && !showAddEmail ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Mail className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-muted-foreground">No Email Accounts Configured</h3>
              <p className="text-sm mt-1">Add an email account to send notifications and reports</p>
              <Button className="mt-4" onClick={() => setShowAddEmail(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Email Account
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {emailConfigs.map((config) => (
            <Card key={config.id} className={config.is_default ? 'border-blue-300' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getProviderIcon(config.provider_type)}
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{config.name}</h4>
                        {config.is_default && (
                          <Badge className="bg-blue-500">Default</Badge>
                        )}
                        <Badge variant="outline" className="capitalize">
                          {config.provider_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{config.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {config.host}:{config.port} • {config.use_ssl ? 'SSL' : 'TLS'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getTestIcon(config.id)}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => testConfig(config.id)}
                      disabled={isTesting}
                    >
                      Test
                    </Button>
                    {!config.is_default && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setDefaultConfig(config.id)}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setEditingConfig({...config});
                        setShowAddEmail(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteConfig(config.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Email Form */}
      {showAddEmail && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingConfig ? 'Edit Email Account' : 'Add New Email Account'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Name</Label>
                <Input 
                  value={configForm.name}
                  onChange={(e) => setConfigForm({...configForm, name: e.target.value})}
                  placeholder="e.g., Company Notifications"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Provider Type</Label>
                <Select 
                  value={configForm.provider_type} 
                  onValueChange={(v) => {
                    const ports = getDefaultPorts(v);
                    setConfigForm({
                      ...configForm, 
                      provider_type: v,
                      port: configForm.use_ssl ? ports.ssl : ports.port
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smtp">SMTP (Gmail, Outlook, etc.)</SelectItem>
                    <SelectItem value="exchange">Microsoft Exchange</SelectItem>
                    <SelectItem value="pop">POP3</SelectItem>
                    <SelectItem value="imap">IMAP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input 
                  type="email"
                  value={configForm.email}
                  onChange={(e) => setConfigForm({...configForm, email: e.target.value})}
                  placeholder="your-email@company.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Password / App Password</Label>
                <div className="relative">
                  <Input 
                    type={showPassword[editingConfig?.id || 'new'] ? 'text' : 'password'}
                    value={configForm.password}
                    onChange={(e) => setConfigForm({...configForm, password: e.target.value})}
                    placeholder="Enter password or app-specific password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPassword({
                      ...showPassword, 
                      [editingConfig?.id || 'new']: !showPassword[editingConfig?.id || 'new']
                    })}
                  >
                    {showPassword[editingConfig?.id || 'new'] ? 
                      <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {configForm.provider_type === 'exchange' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Exchange Server</Label>
                  <Input 
                    value={configForm.exchange_server}
                    onChange={(e) => setConfigForm({...configForm, exchange_server: e.target.value})}
                    placeholder="mail.company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Domain (Optional)</Label>
                  <Input 
                    value={configForm.exchange_domain}
                    onChange={(e) => setConfigForm({...configForm, exchange_domain: e.target.value})}
                    placeholder="COMPANY"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Host / Server</Label>
                  <Input 
                    value={configForm.host}
                    onChange={(e) => setConfigForm({...configForm, host: e.target.value})}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input 
                    type="number"
                    value={configForm.port}
                    onChange={(e) => setConfigForm({...configForm, port: parseInt(e.target.value) || 587})}
                  />
                </div>
                <div className="space-y-2 flex flex-col justify-end">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch 
                        id="use_ssl"
                        checked={configForm.use_ssl}
                        onCheckedChange={(v) => {
                          const ports = getDefaultPorts(configForm.provider_type);
                          setConfigForm({
                            ...configForm, 
                            use_ssl: v,
                            port: v ? ports.ssl : ports.port
                          });
                        }}
                      />
                      <Label htmlFor="use_ssl">SSL</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch 
                        id="use_tls"
                        checked={configForm.use_tls}
                        onCheckedChange={(v) => setConfigForm({...configForm, use_tls: v})}
                      />
                      <Label htmlFor="use_tls">TLS</Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch 
                id="is_default"
                checked={configForm.is_default}
                onCheckedChange={(v) => setConfigForm({...configForm, is_default: v})}
              />
              <Label htmlFor="is_default">Set as default email for sending</Label>
            </div>

            {/* Common Email Presets */}
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">Quick Setup Presets:</p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setConfigForm({
                    ...configForm,
                    provider_type: 'smtp',
                    host: 'smtp.gmail.com',
                    port: 587,
                    use_tls: true,
                    use_ssl: false
                  })}
                >
                  Gmail
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setConfigForm({
                    ...configForm,
                    provider_type: 'smtp',
                    host: 'smtp.office365.com',
                    port: 587,
                    use_tls: true,
                    use_ssl: false
                  })}
                >
                  Office 365
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setConfigForm({
                    ...configForm,
                    provider_type: 'smtp',
                    host: 'smtp-mail.outlook.com',
                    port: 587,
                    use_tls: true,
                    use_ssl: false
                  })}
                >
                  Outlook
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setConfigForm({
                    ...configForm,
                    provider_type: 'smtp',
                    host: 'smtp.mail.yahoo.com',
                    port: 465,
                    use_ssl: true,
                    use_tls: false
                  })}
                >
                  Yahoo
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddEmail(false);
                  setEditingConfig(null);
                  setNewConfig({
                    name: '', provider_type: 'smtp', email: '', password: '',
                    host: '', port: 587, use_ssl: true, use_tls: true, is_default: false,
                    exchange_server: '', exchange_domain: ''
                  });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => saveConfig(configForm)}
                disabled={isSaving || !configForm.name || !configForm.email}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {editingConfig ? 'Update' : 'Save'} Configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// LLM Key Configuration Component
function LLMKeyConfiguration() {
  const [config, setConfig] = useState({
    primary_provider: 'lemonfox',
    lemonfox_api_key: '',
    lemonfox_enabled: true,
    emergent_enabled: false,
    emergent_api_key: '',
    custom_providers: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [newProvider, setNewProvider] = useState({ name: '', api_key: '', endpoint: '' });
  const [showAddProvider, setShowAddProvider] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/llm/config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load LLM config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/llm/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        toast.success('LLM configuration saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save LLM configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const testProvider = async (provider) => {
    setIsTesting(true);
    setTestResults({ ...testResults, [provider]: 'testing' });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/llm/test/${provider}`, {
        method: 'POST'
      });
      const data = await res.json();
      setTestResults({ ...testResults, [provider]: data.success ? 'success' : 'failed' });
      if (data.success) {
        toast.success(`${provider} connection successful`);
      } else {
        toast.error(`${provider} connection failed: ${data.error}`);
      }
    } catch (error) {
      setTestResults({ ...testResults, [provider]: 'failed' });
      toast.error(`Failed to test ${provider}`);
    } finally {
      setIsTesting(false);
    }
  };

  const addCustomProvider = () => {
    if (!newProvider.name || !newProvider.api_key) {
      toast.error('Please enter provider name and API key');
      return;
    }
    setConfig({
      ...config,
      custom_providers: [...config.custom_providers, { ...newProvider, id: Date.now().toString() }]
    });
    setNewProvider({ name: '', api_key: '', endpoint: '' });
    setShowAddProvider(false);
    toast.success('Custom provider added');
  };

  const removeCustomProvider = (id) => {
    setConfig({
      ...config,
      custom_providers: config.custom_providers.filter(p => p.id !== id)
    });
  };

  const getTestIcon = (provider) => {
    const result = testResults[provider];
    if (result === 'testing') return <Loader2 className="h-4 w-4 animate-spin" />;
    if (result === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (result === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
    return null;
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Primary Provider Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            Primary LLM Provider
          </CardTitle>
          <CardDescription>
            Select the primary provider for AI analysis and transcription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Primary Provider for AI Analysis</Label>
            <Select 
              value={config.primary_provider} 
              onValueChange={(v) => setConfig({ ...config, primary_provider: v })}
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lemonfox">LemonFox (Current)</SelectItem>
                <SelectItem value="emergent">Emergent Universal Key</SelectItem>
                {config.custom_providers.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* LemonFox Configuration */}
      <Card className={config.primary_provider === 'lemonfox' ? 'border-purple-200' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Badge variant={config.lemonfox_enabled ? 'default' : 'secondary'}>
                  {config.lemonfox_enabled ? 'Active' : 'Disabled'}
                </Badge>
                LemonFox Configuration
              </CardTitle>
              <CardDescription>Current AI provider for transcription and analysis</CardDescription>
            </div>
            <Switch 
              checked={config.lemonfox_enabled}
              onCheckedChange={(v) => setConfig({ ...config, lemonfox_enabled: v })}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>LemonFox API Key</Label>
            <div className="flex gap-2">
              <Input 
                type="password"
                value={config.lemonfox_api_key || ''}
                onChange={(e) => setConfig({ ...config, lemonfox_api_key: e.target.value })}
                placeholder="Enter LemonFox API key"
                className="flex-1"
              />
              <Button 
                variant="outline" 
                onClick={() => testProvider('lemonfox')}
                disabled={isTesting}
              >
                {getTestIcon('lemonfox')}
                Test
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Used for Whisper transcription and Llama analysis</p>
          </div>
        </CardContent>
      </Card>

      {/* Emergent Configuration */}
      <Card className={config.primary_provider === 'emergent' ? 'border-purple-200' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Badge variant={config.emergent_enabled ? 'default' : 'secondary'}>
                  {config.emergent_enabled ? 'Active' : 'Backup'}
                </Badge>
                Emergent Universal Key
              </CardTitle>
              <CardDescription>Backup provider supporting OpenAI, Anthropic, and Gemini</CardDescription>
            </div>
            <Switch 
              checked={config.emergent_enabled}
              onCheckedChange={(v) => setConfig({ ...config, emergent_enabled: v })}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Emergent API Key</Label>
            <div className="flex gap-2">
              <Input 
                type="password"
                value={config.emergent_api_key || ''}
                onChange={(e) => setConfig({ ...config, emergent_api_key: e.target.value })}
                placeholder="Enter Emergent Universal API key"
                className="flex-1"
              />
              <Button 
                variant="outline" 
                onClick={() => testProvider('emergent')}
                disabled={isTesting}
              >
                {getTestIcon('emergent')}
                Test
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Works with GPT-5.1, Claude Sonnet 4, Gemini 2.5 for text and image generation
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Custom Providers */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Custom LLM Providers</CardTitle>
              <CardDescription>Add additional LLM providers for future use</CardDescription>
            </div>
            <Button variant="outline" onClick={() => setShowAddProvider(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Provider
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {config.custom_providers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No custom providers configured</p>
              <p className="text-sm">Add OpenAI, Anthropic, or other providers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {config.custom_providers.map((provider) => (
                <div key={provider.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">{provider.name}</p>
                    <p className="text-xs text-muted-foreground">{provider.endpoint || 'Default endpoint'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => testProvider(provider.id)}
                    >
                      {getTestIcon(provider.id)}
                      Test
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeCustomProvider(provider.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Provider Form */}
          {showAddProvider && (
            <div className="mt-4 p-4 border rounded-lg space-y-3">
              <h4 className="font-medium">Add New Provider</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider Name</Label>
                  <Input 
                    value={newProvider.name}
                    onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                    placeholder="e.g., OpenAI, Anthropic"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Endpoint (Optional)</Label>
                  <Input 
                    value={newProvider.endpoint}
                    onChange={(e) => setNewProvider({ ...newProvider, endpoint: e.target.value })}
                    placeholder="https://api.example.com/v1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input 
                  type="password"
                  value={newProvider.api_key}
                  onChange={(e) => setNewProvider({ ...newProvider, api_key: e.target.value })}
                  placeholder="Enter API key"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddProvider(false)}>Cancel</Button>
                <Button onClick={addCustomProvider}>Add Provider</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={isSaving}>
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Configuration
        </Button>
      </div>
    </div>
  );
}

// API Keys Configuration Component
function APIKeysConfiguration() {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', scopes: ['read'], expires_in_days: null });
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    setIsLoading(true);
    try {
      const { data } = await getApiKeys();
      setApiKeys(data.api_keys || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyData.name.trim()) {
      toast.error('Please enter a key name');
      return;
    }
    setIsCreating(true);
    try {
      const { data } = await createApiKey(newKeyData);
      setNewlyCreatedKey(data.api_key);
      setShowCreateDialog(false);
      setShowKeyDialog(true);
      setNewKeyData({ name: '', scopes: ['read'], expires_in_days: null });
      fetchApiKeys();
      toast.success('API key created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!confirm('Are you sure you want to delete this API key? This cannot be undone.')) return;
    try {
      await deleteApiKey(keyId);
      toast.success('API key deleted');
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!confirm('Regenerating this key will invalidate the current key. Continue?')) return;
    try {
      const { data } = await regenerateApiKey(keyId);
      setNewlyCreatedKey(data.api_key);
      setShowKeyDialog(true);
      fetchApiKeys();
      toast.success('API key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getScopeBadgeColor = (scope) => {
    switch (scope) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'write': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Key className="h-5 w-5 text-muted-foreground" />
                API Keys
              </CardTitle>
              <CardDescription>
                Manage API keys for programmatic access to your tenant data
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Create API Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="h-12 w-12 mx-auto text-muted-foreground/70 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No API Keys</h3>
              <p className="text-muted-foreground mb-4">Create your first API key to get started with the API</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" /> Create API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="font-medium text-foreground">{key.name}</p>
                      <Badge variant="outline" className={key.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
                        {key.is_active ? 'Active' : 'Revoked'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{key.key_prefix}...</code>
                      <div className="flex gap-1">
                        {key.scopes?.map((scope) => (
                          <Badge key={scope} variant="outline" className={`text-xs ${getScopeBadgeColor(scope)}`}>{scope}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                      {key.expires_at && <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>}
                      {key.last_used_at && <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {key.is_active && (
                      <Button variant="outline" size="sm" onClick={() => handleRegenerateKey(key.id)}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDeleteKey(key.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create API Key</CardTitle>
              <CardDescription>Generate a new API key for programmatic access</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Key Name</Label>
                <Input 
                  placeholder="e.g., Production Server" 
                  value={newKeyData.name} 
                  onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Permissions</Label>
                <div className="flex gap-2 mt-2">
                  {['read', 'write', 'admin'].map((scope) => (
                    <Button
                      key={scope}
                      type="button"
                      variant={newKeyData.scopes.includes(scope) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const scopes = newKeyData.scopes.includes(scope)
                          ? newKeyData.scopes.filter(s => s !== scope)
                          : [...newKeyData.scopes, scope];
                        setNewKeyData({ ...newKeyData, scopes: scopes.length ? scopes : ['read'] });
                      }}
                    >
                      {scope}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Expiration (days)</Label>
                <Select 
                  value={newKeyData.expires_in_days?.toString() || 'never'} 
                  onValueChange={(v) => setNewKeyData({ ...newKeyData, expires_in_days: v === 'never' ? null : parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select expiration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never expires</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateKey} disabled={isCreating}>
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Key
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Show New Key Dialog */}
      {showKeyDialog && newlyCreatedKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" /> API Key Created
              </CardTitle>
              <CardDescription>
                Copy your API key now. You won&apos;t be able to see it again!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-lg border border-border">
                <code className="text-sm break-all text-foreground">{newlyCreatedKey}</code>
              </div>
              <Button 
                className="mt-4 w-full" 
                variant="outline"
                onClick={() => copyToClipboard(newlyCreatedKey)}
              >
                <Copy className="h-4 w-4 mr-2" /> Copy to Clipboard
              </Button>
            </CardContent>
            <div className="flex justify-end p-6 pt-0">
              <Button onClick={() => { setShowKeyDialog(false); setNewlyCreatedKey(null); }}>
                Done
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Audit Logs Viewer Component
function AuditLogsViewer() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({
    action: '',
    start_date: '',
    end_date: '',
    limit: 50,
    offset: 0
  });

  useEffect(() => {
    fetchLogs();
    fetchActions();
  }, [filters]);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const params = { ...filters };
      if (!params.action) delete params.action;
      if (!params.start_date) delete params.start_date;
      if (!params.end_date) delete params.end_date;
      
      const { data } = await getAuditLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      const { data } = await getAuditLogActions();
      setActions(data.actions || []);
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportAuditLogs({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Audit logs exported');
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  };

  const getActionIcon = (action) => {
    if (action?.includes('create') || action?.includes('add')) return <Plus className="h-4 w-4 text-green-500" />;
    if (action?.includes('delete') || action?.includes('remove')) return <Trash2 className="h-4 w-4 text-red-500" />;
    if (action?.includes('update') || action?.includes('edit')) return <RefreshCw className="h-4 w-4 text-blue-500" />;
    if (action?.includes('login') || action?.includes('auth')) return <Key className="h-4 w-4 text-purple-500" />;
    return <Activity className="h-4 w-4 text-muted-foreground" />;
  };

  if (isLoading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Activity className="h-5 w-5 text-muted-foreground" />
                Audit Logs
              </CardTitle>
              <CardDescription>
                Track all actions and changes in your tenant
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchLogs}>
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground">Action Type</Label>
              <Select value={filters.action || 'all'} onValueChange={(v) => setFilters({ ...filters, action: v === 'all' ? '' : v, offset: 0 })}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actions.map((action) => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Start Date</Label>
              <Input 
                type="date" 
                value={filters.start_date} 
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value, offset: 0 })}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">End Date</Label>
              <Input 
                type="date" 
                value={filters.end_date} 
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value, offset: 0 })}
              />
            </div>
            <div className="flex items-end">
              <Button variant="ghost" onClick={() => setFilters({ action: '', start_date: '', end_date: '', limit: 50, offset: 0 })}>
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Logs List */}
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 mx-auto text-muted-foreground/70 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Audit Logs</h3>
              <p className="text-muted-foreground">Activity will be recorded here as you use the platform</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 mb-4">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg border border-border hover:bg-muted transition-colors">
                    <div className="mt-0.5">{getActionIcon(log.action)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground">{log.action}</p>
                        {log.resource_type && (
                          <Badge variant="outline" className="text-xs">{log.resource_type}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {log.user_name || log.user_email || 'System'} 
                        {log.resource_id && ` • ${log.resource_id.substring(0, 8)}...`}
                      </p>
                      {log.details && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">{typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}</p>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={filters.offset === 0}
                    onClick={() => setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) })}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={filters.offset + filters.limit >= total}
                    onClick={() => setFilters({ ...filters, offset: filters.offset + filters.limit })}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// System Refresh Configuration Component
function SystemRefreshConfiguration() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [refreshResults, setRefreshResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSystemStatus();
  }, []);

  const loadSystemStatus = async () => {
    try {
      setIsLoading(true);
      const response = await getSystemStatus();
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to load system status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshAll = async () => {
    try {
      setIsRefreshing(true);
      const response = await refreshAllConfigurations();
      setRefreshResults(response.data);
      await loadSystemStatus();
      toast.success(response.data.message || 'All configurations refreshed successfully');
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Failed to refresh configurations');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            System Configuration Refresh
          </CardTitle>
          <CardDescription>
            Refresh all configurations to ensure changes are live after deployments. This syncs scoring guides, fatal parameters, call types, and other settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Refresh Button */}
          <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
            <Button 
              onClick={handleRefreshAll} 
              disabled={isRefreshing}
              className="gap-2"
              size="lg"
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Refresh All Configurations
                </>
              )}
            </Button>
            <div className="text-sm text-muted-foreground">
              Click to sync all configurations from the codebase to the database
            </div>
          </div>

          {/* Last Refresh Results */}
          {refreshResults && (
            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                {refreshResults.success ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                )}
                Refresh Results
              </h4>
              <div className="grid gap-2">
                {refreshResults.refreshed?.map((item, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    item.status === 'success' ? 'bg-emerald-50 border-emerald-200' :
                    item.status === 'skipped' ? 'bg-slate-50 border-slate-200' :
                    'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {item.status === 'success' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                      )}
                      <span className="font-medium">{item.component.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <Badge variant={item.status === 'success' ? 'default' : 'secondary'}>{item.status}</Badge>
                    </div>
                    {item.details && <p className="text-sm text-muted-foreground mt-1">{item.details}</p>}
                  </div>
                ))}
                {refreshResults.errors?.length > 0 && (
                  <div className="p-3 rounded-lg border bg-red-50 border-red-200">
                    <h5 className="font-medium text-red-700 mb-2">Errors</h5>
                    {refreshResults.errors.map((err, idx) => (
                      <div key={idx} className="text-sm text-red-600">
                        {err.component}: {err.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Current System Status */}
          <div className="space-y-3">
            <h4 className="font-semibold flex items-center gap-2">
              <Server className="h-5 w-5" />
              Current Configuration Status
            </h4>
            {isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading status...
              </div>
            ) : systemStatus?.configurations ? (
              <div className="grid gap-3 md:grid-cols-2">
                {systemStatus.configurations.default_scoring && (
                  <div className="p-4 rounded-lg border bg-card">
                    <h5 className="font-medium mb-2">Default Scoring (AI)</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fatal Parameters:</span>
                        <span>{systemStatus.configurations.default_scoring.fatal_parameters_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Conditions Applied:</span>
                        <span>{systemStatus.configurations.default_scoring.has_conditions_apply ? (
                          <Badge variant="default" className="bg-emerald-600">Yes</Badge>
                        ) : (
                          <Badge variant="destructive">No</Badge>
                        )}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Updated:</span>
                        <span className="text-xs">{systemStatus.configurations.default_scoring.updated_at ? new Date(systemStatus.configurations.default_scoring.updated_at).toLocaleString() : 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                )}
                {systemStatus.configurations.tw_scoring_guide && (
                  <div className="p-4 rounded-lg border bg-card">
                    <h5 className="font-medium mb-2">TW Scoring Guide</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fatal Parameters:</span>
                        <span>{systemStatus.configurations.tw_scoring_guide.fatal_parameters_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Call Types:</span>
                        <span>{systemStatus.configurations.tw_scoring_guide.call_types_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Updated:</span>
                        <span className="text-xs">{systemStatus.configurations.tw_scoring_guide.updated_at ? new Date(systemStatus.configurations.tw_scoring_guide.updated_at).toLocaleString() : 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">No configuration data available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// LLM Credit Usage Component
function LLMUsageTracking() {
  const [isLoading, setIsLoading] = useState(true);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [byProvider, setByProvider] = useState({});
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    loadUsageData();
  }, [days, selectedProvider]);

  const loadUsageData = async () => {
    try {
      setIsLoading(true);
      
      const [dailyRes, providerRes, summaryRes] = await Promise.all([
        getLLMUsageDaily(days, selectedProvider),
        getLLMUsageByProvider(),
        getLLMUsageSummary({ group_by: 'provider' })
      ]);
      
      setDailyUsage(dailyRes.data.daily || []);
      setByProvider(providerRes.data.by_provider || {});
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load LLM usage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toFixed(0) || '0';
  };

  const formatCost = (cost) => {
    return '$' + (cost || 0).toFixed(4);
  };

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                LLM Credit Usage
              </CardTitle>
              <CardDescription>
                Track and analyze AI credit consumption by provider and service
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={loadUsageData}>
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Provider Summary Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Total Summary */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <Cpu className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Requests</p>
                    <p className="text-2xl font-bold">{formatNumber(summary?.totals?.total_requests || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-600 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Tokens</p>
                    <p className="text-2xl font-bold">{formatNumber(summary?.totals?.total_tokens || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-600 rounded-lg">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated Cost</p>
                    <p className="text-2xl font-bold">{formatCost(summary?.totals?.total_cost_usd)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-600 rounded-lg">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                    <p className="text-2xl font-bold">
                      {summary?.totals?.total_requests > 0 
                        ? ((summary?.totals?.successful_requests / summary?.totals?.total_requests) * 100).toFixed(1) + '%'
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Provider Breakdown */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Emergent LLM Key */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                  Emergent LLM Key
                </CardTitle>
                <CardDescription>OpenAI GPT usage through Emergent integration</CardDescription>
              </CardHeader>
              <CardContent>
                {byProvider.emergent ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{formatNumber(byProvider.emergent.total_requests)}</p>
                        <p className="text-xs text-muted-foreground">Requests</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{formatNumber(byProvider.emergent.total_tokens)}</p>
                        <p className="text-xs text-muted-foreground">Tokens</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{formatCost(byProvider.emergent.total_cost_usd)}</p>
                        <p className="text-xs text-muted-foreground">Cost</p>
                      </div>
                    </div>
                    {byProvider.emergent.services && Object.keys(byProvider.emergent.services).length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">By Service</h4>
                        {Object.entries(byProvider.emergent.services).map(([service, data]) => (
                          <div key={service} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                            <span className="text-sm capitalize">{service}</span>
                            <div className="flex gap-4 text-sm text-muted-foreground">
                              <span>{formatNumber(data.requests)} req</span>
                              <span>{formatNumber(data.total_tokens)} tokens</span>
                              <span>{formatCost(data.cost_usd)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No usage data available</p>
                )}
              </CardContent>
            </Card>

            {/* LemonFox */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="w-3 h-3 rounded-full bg-amber-600"></div>
                  LemonFox (Transcription)
                </CardTitle>
                <CardDescription>Audio transcription service usage</CardDescription>
              </CardHeader>
              <CardContent>
                {byProvider.lemonfox ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{formatNumber(byProvider.lemonfox.total_requests)}</p>
                        <p className="text-xs text-muted-foreground">Requests</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{formatNumber(byProvider.lemonfox.total_tokens)}</p>
                        <p className="text-xs text-muted-foreground">Tokens</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-2xl font-bold">{formatCost(byProvider.lemonfox.total_cost_usd)}</p>
                        <p className="text-xs text-muted-foreground">Cost</p>
                      </div>
                    </div>
                    {byProvider.lemonfox.services && Object.keys(byProvider.lemonfox.services).length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">By Service</h4>
                        {Object.entries(byProvider.lemonfox.services).map(([service, data]) => (
                          <div key={service} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                            <span className="text-sm capitalize">{service}</span>
                            <div className="flex gap-4 text-sm text-muted-foreground">
                              <span>{formatNumber(data.requests)} req</span>
                              <span>{data.duration_seconds ? Math.round(data.duration_seconds / 60) + ' min' : '-'}</span>
                              <span>{formatCost(data.cost_usd)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No usage data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Daily Usage Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Daily Usage Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailyUsage.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Date</th>
                        <th className="text-right p-2">Total Requests</th>
                        <th className="text-right p-2">Total Tokens</th>
                        <th className="text-right p-2">Emergent</th>
                        <th className="text-right p-2">LemonFox</th>
                        <th className="text-right p-2">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyUsage.slice().reverse().map((day) => (
                        <tr key={day.date} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-medium">{day.date}</td>
                          <td className="p-2 text-right">{formatNumber(day.total_requests)}</td>
                          <td className="p-2 text-right">{formatNumber(day.total_tokens)}</td>
                          <td className="p-2 text-right text-blue-600">
                            {day.by_provider?.emergent ? formatNumber(day.by_provider.emergent.tokens) : '-'}
                          </td>
                          <td className="p-2 text-right text-amber-600">
                            {day.by_provider?.lemonfox ? formatNumber(day.by_provider.lemonfox.tokens) : '-'}
                          </td>
                          <td className="p-2 text-right font-medium">{formatCost(day.total_cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted font-medium">
                        <td className="p-2">Total</td>
                        <td className="p-2 text-right">{formatNumber(dailyUsage.reduce((s, d) => s + d.total_requests, 0))}</td>
                        <td className="p-2 text-right">{formatNumber(dailyUsage.reduce((s, d) => s + d.total_tokens, 0))}</td>
                        <td className="p-2 text-right text-blue-600">-</td>
                        <td className="p-2 text-right text-amber-600">-</td>
                        <td className="p-2 text-right">{formatCost(dailyUsage.reduce((s, d) => s + d.total_cost_usd, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No usage data recorded yet</p>
                  <p className="text-sm">Usage will appear here once calls are processed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// Data Retention Policy Configuration Component
function RetentionPolicyConfiguration() {
  const { tenant, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [pendingPurge, setPendingPurge] = useState(null);
  const [purgeLogs, setPurgeLogs] = useState([]);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  
  // Form state
  const [retentionDays, setRetentionDays] = useState(30);
  const [warningDays, setWarningDays] = useState(7);
  const [autoPurgeEnabled, setAutoPurgeEnabled] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadPolicy();
  }, []);

  const loadPolicy = async () => {
    setIsLoading(true);
    try {
      const [policyRes, pendingRes] = await Promise.all([
        getTenantRetentionPolicy(),
        getPendingPurgeData()
      ]);
      
      const policyData = policyRes.data;
      setPolicy(policyData);
      setRetentionDays(policyData.retention_days || 30);
      setWarningDays(policyData.warning_days_before || 7);
      setAutoPurgeEnabled(policyData.auto_purge_enabled !== false);
      setPendingPurge(pendingRes.data);
    } catch (error) {
      console.error('Error loading retention policy:', error);
      toast.error('Failed to load retention policy');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateTenantRetentionPolicy({
        retention_days: retentionDays,
        warning_days_before: warningDays,
        auto_purge_enabled: autoPurgeEnabled
      });
      toast.success('Retention policy saved');
      setHasChanges(false);
      loadPolicy();
    } catch (error) {
      toast.error('Failed to save retention policy');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset to global defaults? Your custom settings will be removed.')) return;
    
    try {
      await resetTenantRetentionPolicy();
      toast.success('Reset to global defaults');
      loadPolicy();
    } catch (error) {
      toast.error('Failed to reset policy');
    }
  };

  const handleManualPurge = async () => {
    if (!confirm(`This will permanently delete ${pendingPurge?.total_records || 0} records older than ${retentionDays} days. This cannot be undone. Continue?`)) return;
    
    setIsPurging(true);
    try {
      const res = await triggerManualPurge();
      toast.success(res.data.message);
      loadPolicy();
    } catch (error) {
      toast.error('Failed to purge data');
    } finally {
      setIsPurging(false);
    }
  };

  const loadPurgeLogs = async () => {
    try {
      const res = await getPurgeLogs({ page: 1, page_size: 50 });
      setPurgeLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error loading purge logs:', error);
    }
  };

  const handleChange = (setter) => (value) => {
    setter(value);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Policy Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Data Retention Policy
          </CardTitle>
          <CardDescription>
            Configure how long deleted data is retained before permanent removal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Using Custom vs Global */}
          {policy?.has_custom_policy && (
            <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Using custom tenant policy</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Reset to Global
              </Button>
            </div>
          )}
          
          {!policy?.has_custom_policy && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Using global defaults (Retention: {policy?.global_defaults?.retention_days || 30} days)
              </p>
            </div>
          )}

          {/* Retention Days */}
          <div className="space-y-2">
            <Label htmlFor="retention-days">Retention Period (Days)</Label>
            <div className="flex items-center gap-4">
              <Input
                id="retention-days"
                type="number"
                min="1"
                max="365"
                value={retentionDays}
                onChange={(e) => handleChange(setRetentionDays)(parseInt(e.target.value) || 30)}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">
                Deleted data older than {retentionDays} days will be permanently removed
              </span>
            </div>
          </div>

          {/* Warning Days */}
          <div className="space-y-2">
            <Label htmlFor="warning-days">Warning Before Purge (Days)</Label>
            <div className="flex items-center gap-4">
              <Input
                id="warning-days"
                type="number"
                min="1"
                max="30"
                value={warningDays}
                onChange={(e) => handleChange(setWarningDays)(parseInt(e.target.value) || 7)}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">
                Notify admins {warningDays} days before data is purged
              </span>
            </div>
          </div>

          {/* Auto Purge Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="auto-purge">Automatic Daily Purge</Label>
              <p className="text-sm text-muted-foreground">
                Automatically remove expired data daily at 2:00 AM
              </p>
            </div>
            <Switch
              id="auto-purge"
              checked={autoPurgeEnabled}
              onCheckedChange={handleChange(setAutoPurgeEnabled)}
            />
          </div>

          {/* Save Button */}
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending Purge Preview */}
      {pendingPurge && pendingPurge.total_records > 0 && (
        <Card className="border-amber-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-5 w-5" />
              Data Pending Purge
            </CardTitle>
            <CardDescription>
              The following data is older than {pendingPurge.retention_days} days and will be purged
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold text-foreground">{pendingPurge.deleted_calls}</p>
                <p className="text-xs text-muted-foreground">Deleted Calls</p>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold text-foreground">{pendingPurge.archived_qa_notes}</p>
                <p className="text-xs text-muted-foreground">QA Notes</p>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold text-foreground">{pendingPurge.archived_qa_alerts}</p>
                <p className="text-xs text-muted-foreground">QA Alerts</p>
              </div>
              <div className="p-3 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold text-foreground">{pendingPurge.total_records}</p>
                <p className="text-xs text-muted-foreground">Total Records</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="destructive" 
                onClick={handleManualPurge}
                disabled={isPurging || pendingPurge.total_records === 0}
              >
                {isPurging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Purge Now ({pendingPurge.total_records} records)
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { loadPurgeLogs(); setShowLogsDialog(true); }}
              >
                <Clock className="h-4 w-4 mr-2" />
                View Purge History
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Pending Data */}
      {pendingPurge && pendingPurge.total_records === 0 && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium text-foreground">No Data Pending Purge</p>
              <p className="text-sm text-muted-foreground">
                All deleted data is within the {retentionDays}-day retention period
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Purge History Dialog */}
      {showLogsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto m-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Purge History</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowLogsDialog(false)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {purgeLogs.length > 0 ? (
                <div className="space-y-3">
                  {purgeLogs.map((log) => (
                    <div key={log.id} className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant={log.action === 'auto_purge' ? 'secondary' : 'default'}>
                          {log.action === 'auto_purge' ? 'Auto' : 'Manual'}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm">
                        Purged <strong>{log.total_purged}</strong> records 
                        (Retention: {log.retention_days} days)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        By: {log.triggered_by}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No purge history yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// System Usage Monitoring Component
function SystemUsageMonitoring() {
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [dbMetrics, setDbMetrics] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historyPeriod, setHistoryPeriod] = useState(24);
  const [activeSubTab, setActiveSubTab] = useState('overview');

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchCurrentMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/system-metrics/current`);
      if (res.ok) {
        const data = await res.json();
        setCurrentMetrics(data);
      }
    } catch (error) {
      console.error('Failed to fetch current metrics:', error);
    }
  }, [API_URL]);

  const fetchDbMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/system-metrics/database`);
      if (res.ok) {
        const data = await res.json();
        setDbMetrics(data);
      }
    } catch (error) {
      console.error('Failed to fetch database metrics:', error);
    }
  }, [API_URL]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/system-metrics/history?hours=${historyPeriod}`);
      if (res.ok) {
        const data = await res.json();
        setHistoryData(data.history || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  }, [API_URL, historyPeriod]);

  const recordMetrics = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/system-metrics/record`, { method: 'POST' });
    } catch (error) {
      console.error('Failed to record metrics:', error);
    }
  }, [API_URL]);

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchCurrentMetrics(), fetchDbMetrics(), fetchHistory()]);
    setIsLoading(false);
  }, [fetchCurrentMetrics, fetchDbMetrics, fetchHistory]);

  useEffect(() => {
    loadAllData();
    // Record initial metrics
    recordMetrics();
  }, [loadAllData, recordMetrics]);

  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchCurrentMetrics();
      recordMetrics();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchCurrentMetrics, recordMetrics]);

  useEffect(() => {
    fetchHistory();
  }, [historyPeriod, fetchHistory]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchCurrentMetrics(), fetchDbMetrics(), fetchHistory(), recordMetrics()]);
    setIsRefreshing(false);
    toast.success('Metrics refreshed');
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (percent) => {
    if (percent < 50) return 'text-green-500';
    if (percent < 75) return 'text-yellow-500';
    if (percent < 90) return 'text-orange-500';
    return 'text-red-500';
  };

  const getStatusBg = (percent) => {
    if (percent < 50) return 'bg-green-500';
    if (percent < 75) return 'bg-yellow-500';
    if (percent < 90) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Prepare chart data
  const chartData = historyData.map(item => ({
    time: new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    cpu: item.cpu_percent || 0,
    memory: item.memory_percent || 0,
    disk: item.disk_percent || 0
  }));

  // Pie chart data for disk usage
  const diskPieData = currentMetrics ? [
    { name: 'Used', value: currentMetrics.disk.used_gb, color: '#ef4444' },
    { name: 'Free', value: currentMetrics.disk.free_gb, color: '#22c55e' }
  ] : [];

  // Pie chart data for memory usage
  const memoryPieData = currentMetrics ? [
    { name: 'Used', value: currentMetrics.memory.used_gb, color: '#f59e0b' },
    { name: 'Available', value: currentMetrics.memory.available_gb, color: '#22c55e' }
  ] : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-usage-monitoring">
      {/* Header with controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Gauge className="h-5 w-5 text-blue-500" />
                System Usage Monitoring
              </CardTitle>
              <CardDescription>
                Monitor system resources, performance metrics, and database statistics
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh (30s)</Label>
              </div>
              <Button
                variant="outline"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Sub-tabs for different views */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Gauge className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            History & Trends
          </TabsTrigger>
          <TabsTrigger value="database" className="gap-2">
            <Database className="h-4 w-4" />
            Database
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Current Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CPU Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Cpu className="h-6 w-6 text-blue-600" />
                  </div>
                  <Badge variant="outline" className={getStatusColor(currentMetrics?.cpu?.percent || 0)}>
                    {currentMetrics?.cpu?.percent?.toFixed(1)}%
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold">CPU Usage</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>Cores: {currentMetrics?.cpu?.count}</p>
                  {currentMetrics?.cpu?.frequency_mhz && (
                    <p>Frequency: {currentMetrics.cpu.frequency_mhz.toFixed(0)} MHz</p>
                  )}
                </div>
                <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${getStatusBg(currentMetrics?.cpu?.percent || 0)}`}
                    style={{ width: `${currentMetrics?.cpu?.percent || 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Memory Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Server className="h-6 w-6 text-amber-600" />
                  </div>
                  <Badge variant="outline" className={getStatusColor(currentMetrics?.memory?.percent || 0)}>
                    {currentMetrics?.memory?.percent?.toFixed(1)}%
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold">Memory (RAM)</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>Used: {currentMetrics?.memory?.used_gb} GB</p>
                  <p>Total: {currentMetrics?.memory?.total_gb} GB</p>
                </div>
                <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${getStatusBg(currentMetrics?.memory?.percent || 0)}`}
                    style={{ width: `${currentMetrics?.memory?.percent || 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Disk Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <HardDrive className="h-6 w-6 text-purple-600" />
                  </div>
                  <Badge variant="outline" className={getStatusColor(currentMetrics?.disk?.percent || 0)}>
                    {currentMetrics?.disk?.percent?.toFixed(1)}%
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold">Disk Space</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p>Used: {currentMetrics?.disk?.used_gb} GB</p>
                  <p>Total: {currentMetrics?.disk?.total_gb} GB</p>
                </div>
                <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${getStatusBg(currentMetrics?.disk?.percent || 0)}`}
                    style={{ width: `${currentMetrics?.disk?.percent || 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Uptime Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Timer className="h-6 w-6 text-green-600" />
                  </div>
                  <Badge variant="outline" className="text-green-500">
                    Online
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold">System Uptime</h3>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p className="text-lg font-medium">{currentMetrics?.uptime?.formatted}</p>
                  <p className="text-xs">{Math.floor((currentMetrics?.uptime?.seconds || 0) / 3600)} hours total</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Process & Network Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Process Memory */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Application Process
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">RSS Memory</p>
                    <p className="text-xl font-semibold">{currentMetrics?.process?.memory_rss_mb} MB</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Virtual Memory</p>
                    <p className="text-xl font-semibold">{currentMetrics?.process?.memory_vms_mb} MB</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Network Stats */}
            {currentMetrics?.network && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Network className="h-4 w-4" />
                    Network I/O
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Data Sent</p>
                      <p className="text-xl font-semibold">{formatBytes(currentMetrics.network.bytes_sent)}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Data Received</p>
                      <p className="text-xl font-semibold">{formatBytes(currentMetrics.network.bytes_recv)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Pie Charts for quick visualization */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Memory Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={memoryPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value} GB`}
                      >
                        {memoryPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Disk Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={diskPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value} GB`}
                      >
                        {diskPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* History & Trends Tab */}
        <TabsContent value="history" className="mt-6 space-y-6">
          {/* Period Selector */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Historical Trends</CardTitle>
                <Select value={historyPeriod.toString()} onValueChange={(v) => setHistoryPeriod(parseInt(v))}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Last 6 hours</SelectItem>
                    <SelectItem value="12">Last 12 hours</SelectItem>
                    <SelectItem value="24">Last 24 hours</SelectItem>
                    <SelectItem value="48">Last 48 hours</SelectItem>
                    <SelectItem value="72">Last 72 hours</SelectItem>
                    <SelectItem value="168">Last 7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <CardDescription>
                {historyData.length} data points recorded
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="time" className="text-xs" />
                      <YAxis domain={[0, 100]} className="text-xs" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))', 
                          borderColor: 'hsl(var(--border))' 
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="cpu"
                        name="CPU %"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.3}
                      />
                      <Area
                        type="monotone"
                        dataKey="memory"
                        name="Memory %"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.3}
                      />
                      <Area
                        type="monotone"
                        dataKey="disk"
                        name="Disk %"
                        stroke="#8b5cf6"
                        fill="#8b5cf6"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[350px] flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No historical data yet</p>
                    <p className="text-sm">Metrics will be recorded every 30 seconds</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Peak Usage Info */}
          {chartData.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-muted-foreground">Avg CPU</p>
                  <p className="text-2xl font-bold text-blue-500">
                    {(chartData.reduce((a, b) => a + b.cpu, 0) / chartData.length).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-muted-foreground">Peak CPU</p>
                  <p className="text-2xl font-bold text-blue-500">
                    {Math.max(...chartData.map(d => d.cpu)).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-muted-foreground">Avg Memory</p>
                  <p className="text-2xl font-bold text-amber-500">
                    {(chartData.reduce((a, b) => a + b.memory, 0) / chartData.length).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-muted-foreground">Peak Memory</p>
                  <p className="text-2xl font-bold text-amber-500">
                    {Math.max(...chartData.map(d => d.memory)).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Database Tab */}
        <TabsContent value="database" className="mt-6 space-y-6">
          {/* Database Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Database className="h-5 w-5 text-blue-500" />
                  <p className="text-sm text-muted-foreground">Database Name</p>
                </div>
                <p className="text-lg font-semibold">{dbMetrics?.database_name || 'N/A'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <FolderOpen className="h-5 w-5 text-purple-500" />
                  <p className="text-sm text-muted-foreground">Collections</p>
                </div>
                <p className="text-lg font-semibold">{dbMetrics?.total_collections || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="h-5 w-5 text-green-500" />
                  <p className="text-sm text-muted-foreground">Total Documents</p>
                </div>
                <p className="text-lg font-semibold">{(dbMetrics?.total_documents || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <HardDrive className="h-5 w-5 text-amber-500" />
                  <p className="text-sm text-muted-foreground">Data Size</p>
                </div>
                <p className="text-lg font-semibold">{dbMetrics?.total_size_mb || 0} MB</p>
              </CardContent>
            </Card>
          </div>

          {/* Collection Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Collection Statistics</CardTitle>
              <CardDescription>Document counts and sizes by collection</CardDescription>
            </CardHeader>
            <CardContent>
              {dbMetrics?.collections?.length > 0 ? (
                <>
                  {/* Bar Chart */}
                  <div className="h-[300px] mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dbMetrics.collections.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="name" className="text-xs" angle={-45} textAnchor="end" height={80} />
                        <YAxis className="text-xs" />
                        <Tooltip />
                        <Bar dataKey="document_count" name="Documents" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Table */}
                  <div className="max-h-[400px] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium">Collection</th>
                          <th className="text-right py-2 px-3 font-medium">Documents</th>
                          <th className="text-right py-2 px-3 font-medium">Size</th>
                          <th className="text-right py-2 px-3 font-medium">Avg Doc Size</th>
                          <th className="text-right py-2 px-3 font-medium">Indexes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbMetrics.collections.map((coll) => (
                          <tr key={coll.name} className="border-b border-border/50 hover:bg-muted/50">
                            <td className="py-2 px-3 font-mono text-xs">{coll.name}</td>
                            <td className="text-right py-2 px-3">{(coll.document_count || 0).toLocaleString()}</td>
                            <td className="text-right py-2 px-3">{coll.size_mb?.toFixed(2) || 0} MB</td>
                            <td className="text-right py-2 px-3">{formatBytes(coll.avg_doc_size_bytes || 0)}</td>
                            <td className="text-right py-2 px-3">{coll.index_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No collection data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Storage Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">Data Size</p>
                <p className="text-3xl font-bold text-blue-500">{dbMetrics?.total_size_mb || 0} MB</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">Storage Size</p>
                <p className="text-3xl font-bold text-purple-500">{dbMetrics?.storage_size_mb || 0} MB</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">Index Size</p>
                <p className="text-3xl font-bold text-amber-500">{dbMetrics?.index_size_mb || 0} MB</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function AdminSettings() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('locations');
  const [activeCategory, setActiveCategory] = useState('organization');

  // Get admin info from auth context
  const adminInfo = {
    admin_id: user?.id || user?.user_id,
    tenant_id: user?.tenant_id
  };

  // Organized settings categories with icons and descriptions
  const settingsCategories = [
    {
      id: 'organization',
      label: 'Organization',
      icon: Building2,
      description: 'Manage locations, roles, and team structure',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      tabs: [
        { id: 'locations', label: 'Locations', icon: MapPin, description: 'Manage office locations and regions' },
        { id: 'roles', label: 'Roles & Permissions', icon: Shield, description: 'Configure user roles and access levels' },
      ]
    },
    {
      id: 'security',
      label: 'Security',
      icon: Lock,
      description: 'Authentication, encryption, and compliance',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
      tabs: [
        { id: 'security-compliance', label: 'Security & Compliance', icon: ShieldCheck, description: '2FA, encryption, and compliance report' },
      ]
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: Zap,
      description: 'API keys, LLM configuration, and external services',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      tabs: [
        { id: 'api-keys', label: 'API Keys', icon: Key, description: 'Manage API authentication keys' },
        { id: 'llm', label: 'LLM Configuration', icon: Brain, description: 'Configure AI model settings' },
        { id: 'api', label: 'API Settings', icon: Settings2, description: 'External API configurations' },
      ]
    },
    {
      id: 'audio',
      label: 'Audio & Calls',
      icon: Phone,
      description: 'Audio processing and call settings',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      tabs: [
        { id: 'audio-settings', label: 'Audio & Calls', icon: FileAudio, description: 'Audio decoding and call processing' },
        { id: 'dob-ssn', label: 'DOB/SSN Exceptions', icon: UserCheck, description: 'Manage data access exceptions' },
      ]
    },
    {
      id: 'communication',
      label: 'Communication',
      icon: Mail,
      description: 'Email and notification settings',
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      tabs: [
        { id: 'email', label: 'Email Configuration', icon: Mail, description: 'SMTP and email templates' },
        { id: 'notifications', label: 'Notifications', icon: Megaphone, description: 'Manage alerts and announcements' },
      ]
    },
    {
      id: 'monitoring',
      label: 'Monitoring',
      icon: Gauge,
      description: 'System health and usage tracking',
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      tabs: [
        { id: 'system-usage', label: 'System Usage', icon: Gauge, description: 'CPU, memory, and disk monitoring' },
        { id: 'llm-usage', label: 'LLM Usage', icon: BarChart3, description: 'AI model usage and costs' },
        { id: 'audit-logs', label: 'Audit Logs', icon: Activity, description: 'Activity and change history' },
      ]
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      icon: Server,
      description: 'Data management and system maintenance',
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      tabs: [
        { id: 'retention-policy', label: 'Data Retention', icon: Trash2, description: 'Auto-purge and data lifecycle' },
        { id: 'system-refresh', label: 'System Refresh', icon: RefreshCw, description: 'Clear caches and refresh configs' },
      ]
    },
    {
      id: 'branding',
      label: 'Branding',
      icon: Presentation,
      description: 'Presentations and brand materials',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10',
      tabs: [
        { id: 'presentation', label: 'Presentation', icon: Presentation, description: 'Download Insyra platform overview deck' },
      ]
    },
  ];

  // Find current category and tab info
  const currentCategory = settingsCategories.find(cat => 
    cat.tabs.some(tab => tab.id === activeTab)
  ) || settingsCategories[0];

  const currentTab = currentCategory?.tabs.find(tab => tab.id === activeTab);

  // Handle category change - select first tab in category
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    const category = settingsCategories.find(c => c.id === categoryId);
    if (category && category.tabs.length > 0) {
      setActiveTab(category.tabs[0].id);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl border border-primary/10">
            <Settings className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading text-foreground">Settings</h1>
            <p className="text-muted-foreground text-sm">Configure and manage your workspace</p>
          </div>
        </div>
        {currentTab && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-lg">
            <currentCategory.icon className={`h-4 w-4 ${currentCategory.color}`} />
            <span>{currentCategory.label}</span>
            <span className="text-muted-foreground/50">/</span>
            <span className="font-medium text-foreground">{currentTab.label}</span>
          </div>
        )}
      </div>

      {/* Category Navigation - Horizontal Pills */}
      <div className="flex flex-wrap gap-2 p-1 bg-muted/30 rounded-xl border">
        {settingsCategories.map((category) => {
          const isActive = category.tabs.some(tab => tab.id === activeTab);
          const CategoryIcon = category.icon;
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive 
                  ? `${category.bgColor} ${category.color} shadow-sm` 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <CategoryIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{category.label}</span>
            </button>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar - Tab Navigation within Category */}
        <div className="space-y-2">
          <Card className="overflow-hidden">
            <CardHeader className={`py-4 ${currentCategory.bgColor}`}>
              <div className="flex items-center gap-3">
                <currentCategory.icon className={`h-5 w-5 ${currentCategory.color}`} />
                <div>
                  <CardTitle className="text-base">{currentCategory.label}</CardTitle>
                  <CardDescription className="text-xs mt-0.5">{currentCategory.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2">
              <nav className="space-y-1">
                {currentCategory.tabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                        isActive 
                          ? 'bg-primary text-primary-foreground shadow-sm' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <TabIcon className={`h-4 w-4 ${isActive ? '' : 'opacity-70'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isActive ? '' : ''}`}>{tab.label}</p>
                        <p className={`text-xs truncate ${isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {tab.description}
                        </p>
                      </div>
                      {isActive && (
                        <div className="w-1.5 h-8 bg-primary-foreground/30 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>

          {/* Quick Stats Card */}
          <Card className="hidden lg:block">
            <CardHeader className="py-3">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quick Info</CardTitle>
            </CardHeader>
            <CardContent className="py-2 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tenant ID</span>
                <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">{adminInfo?.tenant_id?.slice(0, 8) || 'N/A'}...</code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Admin</span>
                <span className="text-xs font-medium">{user?.name?.split(' ')[0] || 'Admin'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Role</span>
                <Badge variant="outline" className="text-xs">{user?.role || 'admin'}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="min-w-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Hidden TabsList - we use custom navigation above */}
            <TabsList className="hidden">
              {settingsCategories.flatMap(cat => cat.tabs).map(tab => (
                <TabsTrigger key={tab.id} value={tab.id}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>

            {/* Locations Tab */}
            <TabsContent value="locations" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-blue-500" />
                    <div>
                      <CardTitle>Location Management</CardTitle>
                      <CardDescription>Add, edit, and organize your office locations</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <LocationManagement />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Roles Tab */}
            <TabsContent value="roles" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-blue-500" />
                    <div>
                      <CardTitle>Roles & Permissions</CardTitle>
                      <CardDescription>Define user roles and their access levels</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <RoleManagement />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security & Compliance Tab */}
            <TabsContent value="security-compliance" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-5 w-5 text-emerald-500" />
                    <div>
                      <CardTitle>Security & Compliance</CardTitle>
                      <CardDescription>Two-factor authentication, encryption status, and compliance report</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <SecuritySettings />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audio & Call Settings Tab */}
            <TabsContent value="audio-settings" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <FileAudio className="h-5 w-5 text-green-500" />
                    <div>
                      <CardTitle>Audio & Call Settings</CardTitle>
                      <CardDescription>Configure audio processing and call handling</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <AudioDecodingSettings />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Email Configuration Tab */}
            <TabsContent value="email" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-amber-500" />
                    <div>
                      <CardTitle>Email Configuration</CardTitle>
                      <CardDescription>SMTP settings and email templates</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <EmailConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* API Keys Tab */}
            <TabsContent value="api-keys" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Key className="h-5 w-5 text-purple-500" />
                    <div>
                      <CardTitle>API Keys</CardTitle>
                      <CardDescription>Manage authentication keys for API access</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <APIKeysConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Logs Tab */}
            <TabsContent value="audit-logs" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Activity className="h-5 w-5 text-cyan-500" />
                    <div>
                      <CardTitle>Audit Logs</CardTitle>
                      <CardDescription>View activity history and system changes</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <AuditLogsViewer />
                </CardContent>
              </Card>
            </TabsContent>

            {/* LLM Keys Tab */}
            <TabsContent value="llm" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Brain className="h-5 w-5 text-purple-500" />
                    <div>
                      <CardTitle>LLM Configuration</CardTitle>
                      <CardDescription>Configure AI model API keys and settings</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <LLMKeyConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* API Configuration Tab */}
            <TabsContent value="api" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Settings2 className="h-5 w-5 text-purple-500" />
                    <div>
                      <CardTitle>API Settings</CardTitle>
                      <CardDescription>External API configurations and endpoints</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <APIConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* DOB/SSN Exceptions Tab */}
            <TabsContent value="dob-ssn" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <UserCheck className="h-5 w-5 text-green-500" />
                    <div>
                      <CardTitle>DOB/SSN Exceptions</CardTitle>
                      <CardDescription>Manage user access exceptions for sensitive data</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <DOBSSNExceptionConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* System Refresh Tab */}
            <TabsContent value="system-refresh" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="h-5 w-5 text-red-500" />
                    <div>
                      <CardTitle>System Refresh</CardTitle>
                      <CardDescription>Clear caches and refresh configurations</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <SystemRefreshConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* LLM Usage Tab */}
            <TabsContent value="llm-usage" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="h-5 w-5 text-cyan-500" />
                    <div>
                      <CardTitle>LLM Usage Analytics</CardTitle>
                      <CardDescription>Monitor AI model usage and costs</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <LLMUsageTracking />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Data Retention Policy Tab */}
            <TabsContent value="retention-policy" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Trash2 className="h-5 w-5 text-red-500" />
                    <div>
                      <CardTitle>Data Retention Policy</CardTitle>
                      <CardDescription>Configure automatic data purging and lifecycle</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <RetentionPolicyConfiguration />
                </CardContent>
              </Card>
            </TabsContent>

            {/* System Usage Monitoring Tab */}
            <TabsContent value="system-usage" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Gauge className="h-5 w-5 text-cyan-500" />
                    <div>
                      <CardTitle>System Usage Monitoring</CardTitle>
                      <CardDescription>Real-time CPU, memory, and disk metrics</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <SystemUsageMonitoring />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Settings Tab */}
            <TabsContent value="notifications" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Megaphone className="h-5 w-5 text-amber-500" />
                    <div>
                      <CardTitle>Notification Settings</CardTitle>
                      <CardDescription>Manage alerts, announcements, and notification preferences</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <NotificationSettings 
                    userId={adminInfo?.admin_id}
                    userRole="admin"
                    tenantId={adminInfo?.tenant_id}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Presentation Download Tab */}
            <TabsContent value="presentation" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
              <Card>
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Presentation className="h-5 w-5 text-indigo-500" />
                    <div>
                      <CardTitle>Insyra Platform Presentation</CardTitle>
                      <CardDescription>Download a professional overview deck with live platform data</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <PresentationDownload />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
